export function formatClock(seconds = 0): string {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * A score's name for a person. When the platform has not sent one we say so plainly rather than
 * showing a slice of an internal id — an id tail is not a name and means nothing to an operator.
 */
export function humanScoreName(_scoreId: string | null | undefined, supplied?: string | null): string {
  return supplied?.trim() || 'Untitled score';
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  return formatClock(seconds);
}

/** A score version label shared by controller-backed score lists. */
export function formatScoreVersion(
  versionNumber: number | null | undefined,
  finalizedAt: string | null | undefined,
  locale?: Intl.LocalesArgument,
): string {
  const parts = [typeof versionNumber === 'number' && versionNumber > 0 ? `v${versionNumber}` : 'version unknown'];
  const date = finalizedAt ? new Date(finalizedAt) : undefined;
  if (date && Number.isFinite(date.getTime())) {
    parts.push(new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date));
  }
  return parts.join(' · ');
}

export function timeAgo(value: string | number | null | undefined): string {
  if (value == null) return 'never';
  const at = typeof value === 'number' ? value * (value < 10_000_000_000 ? 1000 : 1) : Date.parse(value);
  if (!Number.isFinite(at)) return 'unknown';
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds} sec ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(at));
}

export function isFreshHeartbeat(value?: string | null, windowMs = 45_000): boolean {
  if (!value) return false;
  return Date.now() - Date.parse(value) <= windowMs;
}

export function percent(value = 0): string {
  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

/** Human-facing finalized rig size: physical devices are runs, while universes remain an output count. */
export function rigSpanSummary(runCount: number, universeCount: number): string {
  return `${runCount.toLocaleString()} ${runCount === 1 ? 'run' : 'runs'} · ` +
    `${universeCount.toLocaleString()} ${universeCount === 1 ? 'universe' : 'universes'}`;
}

export function normalizedColor(rgb?: { r: number; g: number; b: number } | null): string {
  if (!rgb) return '#f1ddb6';
  const values = [rgb.r, rgb.g, rgb.b].map((v) => Math.round(Math.max(0, Math.min(1, v)) * 255));
  return `rgb(${values.join(' ')})`;
}
